import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MonitorSmartphone } from "lucide-react";

// Registers THIS browser as a POS terminal at a chosen location. The server issues the identity (HttpOnly cookie);
// the raw token is kept in localStorage only if the cookie did not stick (fallback header).
export function RegisterDeviceForm({ locations, onDone, compact = false }) {
  const active = (locations || []).filter((l) => l.status === "active");
  const [loc, setLoc] = useState(active.find((l) => !l.at_limit)?.id || active[0]?.id || "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [limitMsg, setLimitMsg] = useState("");
  const chosen = active.find((l) => l.id === loc);
  // Locations can arrive (or be created) after mount — keep the selection valid so the button never sticks disabled.
  useEffect(() => { if (!chosen && active.length) setLoc(active.find((l) => !l.at_limit)?.id || active[0].id); }, [active, chosen]);
  const submit = async (e) => {
    e.preventDefault(); setLimitMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/pos/registers/register-device", { location_id: loc, display_name: name || "Register" });
      localStorage.removeItem("neec_register_token");
      const me = await api.get("/pos/register/me").catch(() => ({ data: {} }));
      if (me.data?.status !== "ok" && data.register_token) localStorage.setItem("neec_register_token", data.register_token);
      window.dispatchEvent(new Event("neec-register-changed"));
            toast.success(data.reused ? "This terminal is already registered and ready for Quick Login" : `"${data.register.display_name}" is registered and ready for Quick Login`);
      window.dispatchEvent(new Event("neec-device-trusted"));
      onDone?.(data.register);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "register_limit") setLimitMsg(d.message); else toast.error(apiError(d));
    } finally { setBusy(false); }
  };
  if (active.length === 0) return <p className="text-sm text-muted-foreground" data-testid="reg-no-locations">No active location yet. The Owner must set up a location first.</p>;
  return (
    <form onSubmit={submit} className={`grid gap-3 ${compact ? "" : "sm:grid-cols-3"}`} data-testid="register-device-form">
      <div><Label className="text-xs">Location</Label>
        <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={loc} onChange={(e) => { setLoc(e.target.value); setLimitMsg(""); }} data-testid="reg-location-select">
          {active.map((l) => <option key={l.id} value={l.id}>{`${l.name} (${l.registers_used}/${l.registers_limit} registers)`}</option>)}
        </select></div>
      <div><Label className="text-xs">Register name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Front Register, Counter 2…" data-testid="reg-name-input" /></div>
      <div className="flex items-end"><Button type="submit" disabled={busy || !loc} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="reg-submit-btn"><MonitorSmartphone className="w-4 h-4 mr-1" /> {busy ? "Registering…" : "Register this device"}</Button></div>
      {chosen?.at_limit && !limitMsg && <p className={`text-xs text-amber-600 ${compact ? "" : "sm:col-span-3"}`} data-testid="reg-at-limit-hint">{chosen.name} is at its register limit ({chosen.registers_used}/{chosen.registers_limit}).</p>}
      {limitMsg && <div className={`rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm ${compact ? "" : "sm:col-span-3"}`} data-testid="reg-limit-message"><p className="font-bold">Register limit reached</p><p className="text-muted-foreground mt-1">{limitMsg.replace(/^Register limit reached\.\s*/, "")}</p></div>}
    </form>
  );
}
